import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "./api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=loading, false=guest, obj=authed
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If returning from Emergent OAuth, skip /me — AuthCallback will handle
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    // Fallback watchdog for iOS Capacitor / WKWebView: on the very first
    // launch we have seen `/api/auth/me` hang indefinitely (no response,
    // no error) when `withCredentials: true` is combined with the
    // `capacitor://localhost` origin — likely a WebKit CORS-with-credentials
    // edge case. Without this timer, `loading` stays true forever, every
    // `<Protected>` route renders "Loading…" and never mounts (so
    // /request never opens, /account is dead, and every downstream
    // useEffect that depends on the user staying idle blocks React
    // rerenders app-wide). Safari desktop resolves /me in < 300 ms so
    // this watchdog never fires there — behavior for the web is
    // unchanged.
    const watchdog = setTimeout(() => {
      setLoading((cur) => (cur ? false : cur));
    }, 8000);
    checkAuth().finally(() => clearTimeout(watchdog));
    return () => clearTimeout(watchdog);
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.token) localStorage.setItem("haulhub_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    if (data.token) localStorage.setItem("haulhub_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("haulhub_token");
    setUser(false);
  };

  return (
    <AuthCtx.Provider value={{ user, loading, login, register, logout, refresh: checkAuth, setUser }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
